


































































// vue
import {
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
} from '@vue/composition-api';
import Chart from 'chart.js/auto';

// components
import Button from 'primevue/button';
import Panel from 'primevue/panel';
import InputText from 'primevue/inputtext';

// services
import { useService } from '@/vue3/api/services';

// config
import Tools from '@/vue3/configs/table/locationToolConfig';

// hooks
import { useNotify } from '@/vue3/hooks/notifyHook';
import { useStore } from '@/store';

// turf
import * as turf from '@turf/turf';

// mapbox
import mapboxgl from 'mapbox-gl';

export default defineComponent({
    components: {
        Button,
        Panel,
        InputText,
    },
    props: {
        mapbox: {
            type: Object,
            default: () => {},
        },
        setMapboxElevation: {
            type: Function,
            default: () => {},            
        },
    },
    setup(props: any) {
        // services
        const { getGeoRecord } = useService();

        // hooks
        const { errorToast, successToast } = useNotify();
        const store = useStore();

        // refs
        const activeToolName = ref<string>('');
        const activeToolDesc = ref<string>('');

        const isInputLocationTool = ref<boolean>(false);

        const latitude = ref();
        const longitude = ref();
        const marker = ref();
        const features = ref();

        const elevationFactor = ref(store.state.isMeters ? 1 : 1.09361);

        // methods
        const toolClick = (tool: any): void => {
            activeToolName.value = tool.name;
            activeToolDesc.value = tool.description;

            if (activeToolName.value == 'Location Tool') {
                props.mapbox.map.off('click', onMouseMoveOnLine);
                props.mapbox.map.off('touchend', onMouseMoveOnLine);

                props.mapbox.map.on('mousemove', onMouseMoveOnMap);
                props.mapbox.map.on('touchstart', onMouseMoveOnMap);
                props.mapbox.map.on('touchend', copyCoordinatesToClipboard);

                props.mapbox.map
                    .getCanvas()
                    .addEventListener('mouseleave', onMouseLeaveMap);
                props.mapbox.map.on('click', copyCoordinatesToClipboard);

                if (marker.value) {
                    marker.value.remove();
                }

                isInputLocationTool.value = false;
                // props.mapbox.isElevation = false;
                props.setMapboxElevation(false)
            } else if (activeToolName.value == 'Line Elevation') {
                props.mapbox.map.off('mousemove', onMouseMoveOnMap);
                props.mapbox.map.off('touchstart', onMouseMoveOnMap);
                props.mapbox.map.off('click', copyCoordinatesToClipboard);
                props.mapbox.map.off('touchend', copyCoordinatesToClipboard);

                props.mapbox.map.on('click', onMouseMoveOnLine);
                props.mapbox.map.on('touchend', onMouseMoveOnLine);

                isInputLocationTool.value = false;
            } else {
                turnOffMouseEvents();

                isInputLocationTool.value = true;
            }
        };

        const onMouseMoveOnLine = (e: any): void => {
            features.value = props.mapbox.map.queryRenderedFeatures(e.point);

            features.value.map(async (feature: any) => {
                if (feature.layer.id === 'transparent-geo-record-linestring') {
                    const geoRecord = await getGeoRecord(feature.id);

                    if (geoRecord) {
                        let lineData = {
                            type: 'Feature',
                            geometry: geoRecord.geometry,
                            properties: {},
                        };

                        if (lineData.geometry.coordinates.length > 0) {
                            updateElevationProfile(lineData);
                        }
                    }
                }
            });
        };

        const updateElevationProfile = (lineData: any) => {
            let elevations: any = [];
            let xDistance;
            const chartCanvas = document.getElementById(
                'myChart',
            ) as HTMLCanvasElement;

            Chart.getChart(chartCanvas)?.destroy();

            const getDistance = (coord1: any, coord2: any) => {
                return turf.distance(turf.point(coord1), turf.point(coord2), {
                    units: 'meters',
                });
            };

            const getElevation = (coord: any) => {
                return (
                    props.mapbox.map.queryTerrainElevation(coord) *
                    elevationFactor.value
                );
            };

            if (lineData.geometry.coordinates.length > 2) {
                const firstPoint = lineData.geometry.coordinates[0];
                const lastPoint =
                    lineData.geometry.coordinates[
                        lineData.geometry.coordinates.length - 1
                    ];
                const distance = getDistance(firstPoint, lastPoint);
                xDistance = distance;
                const numPoints = Math.max(2, Math.ceil(distance / 10));

                for (let i = 0; i <= numPoints; i++) {
                    const ratio = i / numPoints;
                    const coord = [
                        firstPoint[0] + ratio * (lastPoint[0] - firstPoint[0]),
                        firstPoint[1] + ratio * (lastPoint[1] - firstPoint[1]),
                    ];
                    const elevation = getElevation(coord);
                    elevations.push(elevation.toFixed());
                }
            } else {
                const start = lineData.geometry.coordinates[0];
                const end = lineData.geometry.coordinates[1];
                const distance = getDistance(start, end);
                xDistance = distance;
                const numPoints = Math.max(2, Math.ceil(distance / 10));

                for (let i = 0; i <= numPoints; i++) {
                    const ratio = i / numPoints;
                    const coord = [
                        start[0] + ratio * (end[0] - start[0]),
                        start[1] + ratio * (end[1] - start[1]),
                    ];
                    const elevation = getElevation(coord);
                    elevations.push(elevation.toFixed());
                }
            }
            const stepSize = xDistance / (elevations.length - 1)
            const xValues = Array.from({ length: elevations.length }, (_, i) => (i * stepSize).toFixed(1)); // Generate x values

            if (elevations.length > 1) {
                const elevationChart = new Chart(chartCanvas, {
                    type: 'line',
                    data: {
                        labels: xValues,
                        datasets: [
                            {
                                data: elevations,
                                fill: false,
                                tension: 0.1,
                                borderWidth: 3,
                                borderColor: '#fff',
                                pointRadius: 0,
                                label: `Elevation (${
                                    store.state.isMeters ? 'm' : 'yd'
                                })`,
                            },
                        ],
                    },
                    options: {
                        plugins: {
                            legend: {
                                display: false,
                            },
                            title: {
                                display: true,
                                align: 'center',
                                text: `Elevation (${
                                    store.state.isMeters ? 'm' : 'yd'
                                })`,
                                color: 'rgba(225,226,225, 0.8)',
                            },
                        },
                        maintainAspectRatio: false,
                        responsive: true,
                        scales: {
                            x: {
                                grid: {
                                    display: true,
                                    color: 'rgba(225,226,225, 0.3)',
                                },
                                ticks: {
                                    color: 'rgba(225,226,225, 0.8)',
                                },
                                title: {
                                    display: true,
                                align: 'center',
                                text: `Distance (${
                                    store.state.isMeters ? 'm' : 'yd'
                                })`,
                                color: 'rgba(225,226,225, 0.8)',
                                }
                            },
                            y: {
                                grid: {
                                    display: true,
                                    color: 'rgba(225,226,225, 0.3)',
                                },
                                ticks: {
                                    color: 'rgba(225,226,225, 0.8)',
                                },
                            },
                        },

                        layout: {
                            padding: 10,
                        },
                    },
                });
                elevationChart.data.datasets[0] = {
                    data: elevations,
                    fill: false,
                    tension: 0.1,
                    borderWidth: 3,
                    borderColor: '#fff',
                    pointRadius: 0,
                    label: `Elevation (${store.state.isMeters ? 'm' : 'yd'})`,
                };
                elevationChart.update();
            }

            // props.mapbox.isElevation = true;
            props.setMapboxElevation(true)
        };

        const zoomToLocation = (event: any): void => {
            event?.stopPropagation();

            if (isNaN(longitude.value) || isNaN(latitude.value)) {
                errorToast('Invalid longitude or latitude.');
                return;
            }

            let point = turf.point([longitude.value, latitude.value]);
            let bbox = turf.bbox(point);

            props.mapbox.map.fitBounds(bbox, {
                padding: { top: 100, bottom: 100, left: 175, right: 175 },
                maxZoom: 19,
            });

            if (marker.value) {
                marker.value.remove();
            }

            marker.value = new mapboxgl.Marker({ color: '#1f2d40' })
                .setLngLat([longitude.value, latitude.value])
                .addTo(props.mapbox.map);
        };

        const popup = new mapboxgl.Popup({
            closeButton: false,
        });

        const initPopUp = (): void => {
            popup.value = new mapboxgl.Popup({
                closeButton: false,
                className: 'pop-up',
                maxWidth: '320px',
            });
        };

        const getElevation = (lngLat: any) => {
            const unit = store.state.isMeters ? 'm' : 'yd';

            let elevation = props.mapbox.map.queryTerrainElevation(lngLat, {
                exaggerated: false,
            });
            let calculatedElevation = (
                elevation * elevationFactor.value
            ).toFixed(2);

            return `${calculatedElevation}${unit}`;
        };

        const onMouseMoveOnMap = (e: any): void => {
            props.mapbox.map.getCanvas().style.cursor = 'pointer';
            let calculatedZValue = getElevation(e.lngLat);

            let popupContent = `X: ${e.lngLat.lng.toFixed(
                7,
            )} </br> Y: ${e.lngLat.lat.toFixed(
                7,
            )} </br> Z: ${calculatedZValue}`;

            popup.value
                .setLngLat(e.lngLat)
                .setHTML(popupContent)
                .addTo(props.mapbox.map);
        };

        const copyCoordinatesToClipboard = (e: any): void => {
            let calculatedZValue = getElevation(e.lngLat);

            navigator.clipboard
                .writeText(
                    `X: ${e.lngLat.lng.toFixed(7)} \n Y: ${e.lngLat.lat.toFixed(
                        7,
                    )} \n Z: ${calculatedZValue}`,
                )
                .then(() => {
                    if (navigator.clipboard) {
                        if (!store.state.isMobile) {
                            successToast('Coordinates copied to clipboard.');
                        }
                    }
                })
                .catch(() => {
                    errorToast('Failed to copy coordinates to clipboard:');
                });
        };

        const onMouseLeaveMap = (): void => {
            props.mapbox.map.getCanvas().style.cursor = '';
            popup.value.remove();
        };

        const turnOffMouseEvents = () => {
            props.mapbox.map.off('touchstart', onMouseMoveOnMap);
            props.mapbox.map.off('mousemove', onMouseMoveOnMap);
            props.mapbox.map.off('click', copyCoordinatesToClipboard);
            props.mapbox.map.off('touchend', copyCoordinatesToClipboard);
            props.mapbox.map.off('mousemove', onMouseMoveOnLine);
            props.mapbox.map.off('touchend', onMouseMoveOnLine);

            popup.value.remove();
            // props.mapbox.isElevation = false;
            props.setMapboxElevation(false);
        };

        // lifecycle
        onMounted(() => {
            toolClick(Tools[0]);
            initPopUp();
        });

        onUnmounted(() => {
            turnOffMouseEvents();
        });

        return {
            Tools,
            activeToolName,
            activeToolDesc,
            toolClick,
            isInputLocationTool,
            latitude,
            longitude,
            zoomToLocation,
        };
    },
});
